import { Injectable, signal, computed, Signal } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { HttpClient, HttpParams } from '@angular/common/http';
import { User } from '@core/models/user';
import { environment } from 'environments/environment';
import { RouteConstants } from '@core/constants/route.constants';
import { ObjectUtils } from '@core/utils/object-utils.component';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  // Signal declarations
  private userSignal = signal<User | null>(null);
  private refreshPasswordSignal = signal<User | null>(null);

  // Computed signals (readonly)
  public readonly user = computed(() => this.userSignal());

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {
    // Initialize from localStorage
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      this.userSignal.set(JSON.parse(storedUser));
    }
  }

  // User section
  get currentUser(): User | null {
    return this.userSignal();
  }

  updateCurrentUser = (user: User): void => {
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.userSignal.set(user);
  }

  impersonate(username: string): Observable<User> {
    const params = new HttpParams().set('userName', username);

    return this.http.get<User>(`${environment.PackagingUrl}/api/user/impersonate`, { params }).pipe(
      map((user) => {
        const currentUser = this.currentUser;
        const impersonatedUser: User = {
          ...user,
          impersonatedFrom: currentUser ?? undefined
        };

        this.updateCurrentUser(impersonatedUser);
        return impersonatedUser;
      })
    );
  }

  backImpersonate = (): void => {
    const user: any = this.currentUser?.impersonatedFrom;
    if (!ObjectUtils.isNullOrUndefined(user)) {
      this.updateCurrentUser(user);
    }
  }

  // Password section
  get refreshPasswordData(): Signal<User | null> {
    return computed(() => this.refreshPasswordSignal());
  }

  setRefreshPasswordData = (data: User | null): void => {
    this.refreshPasswordSignal.set(data);
  }

  // Token section
  setReleaseTokenLocalStorage = (token: string): void => {
    localStorage.setItem('releaseToken', token.toString());
  }

  // Endpoints
  login = (username: string, password: string): Observable<User> => {
    return this.http.post<User>(`${environment.PackagingUrl}/api/user/authenticate`, { username, password })
      .pipe(
        map((user) => {
          this.updateCurrentUser(user);
          if (user.passwordExpired) {
            this.setRefreshPasswordData(user);
          }
          return user;
        })
      );
  }

  logout = (): void => {
    // remove user from local storage to log user out
    localStorage.removeItem('currentUser');
    localStorage.removeItem('releaseToken');
    this.userSignal.set(null);
    this.refreshPasswordSignal.set(null);
    this.router.navigate([RouteConstants.LOGIN_MODULE.LOGIN]);
  }

}
